const { SITE_BASE_URL, buildAbsoluteSiteUrl } = require('./brokerConnectionSource');
const { CORE_SUBSCRIPTION_PLANS } = require('./publicPricingSource');

function absoluteUrl(value, baseUrl = SITE_BASE_URL) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return buildAbsoluteSiteUrl(value, baseUrl);
}

function breadcrumbName(pathname, title) {
  if (pathname === '/') return 'Home';
  if (title) return title.replace(/\s+\|\s+BotSpot.*$/i, '');
  return pathname.split('/').filter(Boolean).pop().replace(/-/g, ' ');
}

function buildBreadcrumbs(route, baseUrl = SITE_BASE_URL) {
  const parts = route.path.split('/').filter(Boolean);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: baseUrl,
    },
  ];

  let currentPath = '';
  parts.forEach((part, index) => {
    currentPath += `/${part}`;
    const isLast = index === parts.length - 1;
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: isLast
        ? breadcrumbName(currentPath, route.content?.heading || route.title)
        : breadcrumbName(currentPath),
      item: buildAbsoluteSiteUrl(currentPath, baseUrl),
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function organizationId(baseUrl = SITE_BASE_URL) {
  return `${buildAbsoluteSiteUrl('/', baseUrl)}#organization`;
}

function organizationForHomepage(baseUrl = SITE_BASE_URL) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': organizationId(baseUrl),
    name: 'Lumiwealth, Inc.',
    alternateName: 'BotSpot',
    url: buildAbsoluteSiteUrl('/', baseUrl),
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'contact@botspot.trade',
      url: buildAbsoluteSiteUrl('/contact', baseUrl),
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
    },
  };
}

function softwareApplicationForHomepage(route, baseUrl = SITE_BASE_URL) {
  const prices = CORE_SUBSCRIPTION_PLANS.map((tier) => tier.price).filter((price) =>
    Number.isFinite(price),
  );
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'BotSpot',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web, iOS, Android',
    url: baseUrl,
    description: route.description,
    provider: { '@id': organizationId(baseUrl) },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: Number(lowPrice.toFixed(2)),
      highPrice: Number(highPrice.toFixed(2)),
      offerCount: CORE_SUBSCRIPTION_PLANS.length,
    },
  };
}

function softwareApplicationForStrategy(route, baseUrl = SITE_BASE_URL) {
  const canonical = buildAbsoluteSiteUrl(route.path, baseUrl);
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: route.content?.heading || route.title,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    url: canonical,
    description: route.description,
    isPartOf: {
      '@type': 'SoftwareApplication',
      name: 'BotSpot',
      url: baseUrl,
    },
  };
}

function structuredDataForRoute(route, baseUrl = SITE_BASE_URL) {
  const canonical = buildAbsoluteSiteUrl(route.path, baseUrl);
  const isArticle = route.pageType === 'article';
  const isHomepage = route.path === '/';
  const isMarketplaceStrategy = Boolean(route.path?.startsWith('/marketplace/strategy/'));
  let primary;
  if (isHomepage) {
    primary = softwareApplicationForHomepage(route, baseUrl);
  } else if (isMarketplaceStrategy || route.schemaType === 'SoftwareApplication') {
    primary = softwareApplicationForStrategy(route, baseUrl);
  } else {
    primary = {
      '@context': 'https://schema.org',
      '@type': isArticle ? 'Article' : 'WebPage',
      headline: route.content?.heading || route.title,
      name: route.title,
      description: route.description,
      url: canonical,
      mainEntityOfPage: canonical,
      publisher: {
        '@type': 'Organization',
        name: 'BotSpot',
        url: baseUrl,
      },
    };
  }

  if (isArticle && route.lastmod && primary['@type'] === 'Article') {
    primary.datePublished = route.lastmod;
    primary.dateModified = route.lastmod;
  }
  if (
    Array.isArray(route.content?.sources) &&
    route.content.sources.length > 0 &&
    primary['@type'] !== 'SoftwareApplication'
  ) {
    primary.citation = route.content.sources.map((source) => absoluteUrl(source.url, baseUrl));
  }

  const graph = [primary];

  if (route.path === '/') {
    graph.push(organizationForHomepage(baseUrl));
  } else {
    graph.push(buildBreadcrumbs(route, baseUrl));
  }

  if (Array.isArray(route.content?.faq) && route.content.faq.length > 0) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: route.content.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return graph;
}

module.exports = {
  buildBreadcrumbs,
  organizationForHomepage,
  softwareApplicationForHomepage,
  softwareApplicationForStrategy,
  structuredDataForRoute,
};
